import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Check, Star, ArrowLeft, ShieldCheck, ChevronDown, Volume2, Lightbulb, Sliders, Music, Send, Building2 } from 'lucide-react'
import ContactForm from '../components/ContactForm'
import ReviewCard from '../components/ReviewCard'
import SEO from '../components/SEO'

function WeddingChecklist() {
  const phases = [
    {
      id: "12-18",
      title: "12-18 måneder før",
      items: [
        "Læg et budget",
        "Kig på en gæsteliste",
        "Vælg en dato og reservér feststed",
        "Bestil tid i kirken eller på rådhuset",
        "Book en blomsterhandler",
        "Book fotograf til bryllupsbilleder og eventuelt også videoer",
        "Book musik, DJ eller band, og eventuelt underholdning",
        "Kig på brudekjoler og tøj til brudgommen"
      ]
    },
    {
      id: "9-12",
      title: "9-12 måneder før",
      items: [
        "Bestil mad og kok",
        "Book serveringspersonale",
        "Vælg en forlover",
        "Vælg brudepiger",
        "Planlæg jeres bryllupsrejse",
        "Vælg en brudekjole",
        "Arranger transport til og fra kirken"
      ]
    },
    {
      id: "6-9",
      title: "6-9 måneder før",
      items: [
        "Færdiggør gæstelisten",
        "Send 'save-the-date' ud til dine gæster",
        "Klargør bryllupsinvitationerne",
        "Book et møde med præsten",
        "Bestil musik til kirken",
        "Overnatning til bryllupsnatten",
        "Bestil dekorationer og buket"
      ]
    },
    {
      id: "4-6",
      title: "4-6 måneder før",
      items: [
        "Lav en ønskeseddel",
        "Send invitationer ud",
        "Shop jeres vielsesringe",
        "Bestil bryllupskage",
        "Find en make-up artist/frisør og hold en prøvedag",
        "Øv brudevalsen",
        "Skriv jeres løfter til hinanden"
      ]
    },
    {
      id: "0-4",
      title: "0-4 måneder før",
      items: [
        "Begynd på bordplanen",
        "Bestem dig for den endelige menu",
        "Lav bordkort",
        "Gå dine brudesko til",
        "Køb morgengaver",
        "Prøv tøjet en sidste gang",
        "Hold møde med toastmasteren"
      ]
    }
  ]

  const [activeTab, setActiveTab] = useState("12-18")
  const [checkedItems, setCheckedItems] = useState({})

  const toggleItem = (phaseId, index) => {
    const key = `${phaseId}-${index}`
    setCheckedItems((prev) => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const totalItems = phases.reduce((acc, phase) => acc + phase.items.length, 0)
  const completedItems = Object.values(checkedItems).filter(Boolean).length
  const progressPercent = Math.round((completedItems / totalItems) * 100)

  return (
    <div className="w-full rounded-2xl border border-zinc-900 bg-zinc-950 p-6 md:p-8 space-y-8 shadow-2xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl font-bold text-white">Bryllupsplanlægning</h3>
          <p className="text-sm text-zinc-400 mt-1">Tips og tjekliste til jeres bryllup</p>
        </div>
        <div className="flex items-center gap-3 bg-zinc-900/50 px-4 py-2 rounded-xl border border-zinc-800">
          <div className="text-right">
            <span className="text-xs text-zinc-500 block">Jeres fremskridt</span>
            <span className="text-sm font-bold text-[#bb9b56]">{completedItems} af {totalItems} udført</span>
          </div>
          <div className="relative h-10 w-10 shrink-0 flex items-center justify-center rounded-full border-2 border-zinc-800">
            <span className="text-xs font-bold text-white">{progressPercent}%</span>
          </div>
        </div>
      </div>

      <div className="w-full h-2 bg-zinc-900 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#bb9b56] to-yellow-400 transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className="flex flex-wrap gap-2 border-b border-zinc-900 pb-4">
        {phases.map((phase) => (
          <button
            key={phase.id}
            type="button"
            onClick={() => setActiveTab(phase.id)}
            aria-pressed={activeTab === phase.id}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
              activeTab === phase.id
                ? "bg-[#bb9b56] text-white"
                : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-white"
            }`}
          >
            {phase.title}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-h-[220px]">
        {phases
          .find((p) => p.id === activeTab)
          ?.items.map((item, index) => {
            const isChecked = !!checkedItems[`${activeTab}-${index}`]
            return (
              <div
                key={index}
                onClick={() => toggleItem(activeTab, index)}
                className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                  isChecked
                    ? "bg-amber-500/5 border-amber-500/20 text-zinc-400"
                    : "bg-zinc-900/30 border-zinc-900 hover:border-zinc-800 text-white"
                }`}
              >
                <div
                  className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 transition-all ${
                    isChecked
                      ? "bg-[#bb9b56] border-[#bb9b56] text-white"
                      : "border-zinc-700 bg-zinc-950"
                  }`}
                >
                  {isChecked && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                </div>
                <span className={`text-sm md:text-base leading-relaxed ${isChecked ? "line-through" : ""}`}>
                  {item}
                </span>
              </div>
            )
          })}
      </div>

      <p className="text-xs text-zinc-500 leading-relaxed">
        Hvis ikke I selv ønsker at stå for planlægning eller måske kun dele af den, kan I vælge at bruge en wedding planner. En professionel wedding planner kan hjælpe med hele planlægningen, så I får en uforglemmelig dag.
      </p>
    </div>
  )
}

const SERVICES_DATA = {
  firmafest: {
    slug: 'firmafest',
    canonicalPath: '/lej-dj-til-firmafest/',
    title: 'Professionel DJ til Firmafest & Fredagsbar i København og på Sjælland',
    subtitle: 'Skab en uforglemmelig firmaevent med en erfaren DJ, der forstår at skabe en fest og samle medarbejderne på dansegulvet.',
    cta: 'Få et tilbud på DJ firmafest',
    image: 'https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/djron9-firmafest.webp',
    imagePosition: 'object-center',
    formHeading: 'Book DJ Ron9 til jeres næste firmafest',
    references: [
      'Udenrigsministeriet',
      'Novo',
      'Mærsk',
      'Nordea',
      'Synoptik',
      'SAS Radisson',
      'COWI',
      'Niras',
      'Hotel Marienlyst',
      'Coloplast',
      'Statoil',
      'Circle K',
      'TDC',
      'Zoologisk Have',
      'IBM',
      'Medtronic',
      'Tivoli Congres Center'
    ],
    sections: [
      {
        heading: 'Den perfekte musikalske ramme om jeres firmafest',
        paragraphs: [
          'En vellykket firmafest eller en eksklusiv fredagsbar kræver en helt særlig fingerspidsfornemmelse fra DJ’en. Musikken må aldrig bare være en standard-playliste, der kører i baggrunden. Den skal tilpasses jeres ønsker, gæsternes sammensætning og festens naturlige dynamik.',
          'Når I booker DJ Ron9 til jeres næste firma event, får I en professionel DJ med over 25 års erfaring der forstår vigtigheden af en glidende overgang i løbet af aftenen: Fra den sofistikerede og dæmpede lounge-musik under velkomstreceptionen og middagen, hvor jeres medarbejdere skal kunne netværke og tale sammen – til det øjeblik, hvor lyset dæmpes, og dansegulvet transformeres til en energisk fest.'
        ]
      },
      {
        heading: 'Musik, der rammer bredt på tværs af generationer',
        paragraphs: [
          'I en virksomhed er medarbejderstaben ofte sammensat af mange forskellige aldersgrupper. Udfordringen til en firmafest er at ramme en musikprofil, hvor alle aldersgrupper føler sig underholdt og får lyst til at danse.',
          'Mit musikarkiv spænder over 8 årtier. Jeg mixer elegant de største klassikere fra 80’erne og 90’erne med sprøde club-baskere, funk, RnB og nutidens største radiohits. Fordi jeg har arbejdet professionelt med musik i mere end 25 år, har jeg opbygget en tårnhøj erfaring og ekspertise, der sikrer at jeg hele tiden læser rummet og justerer musikken, så dansegulvet forbliver fyldt hele natten.'
        ]
      },
      {
        heading: 'Pålidelighed og pro-audio udstyr i topklasse',
        paragraphs: [
          'Når I overlader musikken til DJ Ron9, overlader I den til en moden og professionel DJ. Jeg møder altid op i god tid, er klædt passende til jeres arrangement og medbringer udelukkende godkendt, high-end lydanlæg og et stilrent, moderne lysshow, der tilpasses lokalets størrelse.',
          'Uanset om I planlægger en intim fredagsbar i Storkøbenhavn eller den helt store årlige gallafest for hundreder af ansatte på Sjælland, skræddersyr jeg løsningen i tæt dialog med jeres eventansvarlige eller HR-afdeling.'
        ]
      }
    ]
  },
  bryllup: {
    slug: 'bryllup',
    canonicalPath: '/dj-til-bryllup/',
    title: 'Erfaren DJ til bryllup i København og på Sjælland – Tryghed på jeres største aften',
    subtitle: 'Gør jeres bryllupsfest uforglemmelig med DJ Ron9. Over 25 års erfaring sikrer professionel musikalsk afvikling fra middagen til brudevalsen og den vilde dansefest.',
    cta: 'Få et tilbud på bryllup',
    image: 'https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/djron9-bryllup.webp',
    imagePosition: 'object-center',
    formHeading: 'Få et tilbud på DJ til bryllup',
    isWedding: true,
    sections: [
      {
        heading: 'Total tryghed og skræddersyet musik til jeres bryllup',
        paragraphs: [
          'Jeres bryllup er en af livets mest betydningsfulde og mindeværdige begivenheder. Når de officielle indslag, talerne og middagen er vel overstået, er det musikken, der skal binde hele aftenen sammen og holde gæsterne på dansegulvet hele natten.',
          'Som jeres dedikerede DJ til bryllup, er mit vigtigste mål at skabe total tryghed for jer som brudepar. Det hele starter med en grundig forventningsafstemning og tæt dialog i god tid inden den store dag. Jeg gennemgår jeres personlige musiksmag, jeres yndlingsnumre og de sange, som absolut ikke skal spilles (no-go-lister) i tæt dialog med jer. På den måde sikrer jeg, at soundtracket til jeres bryllup bliver præcis, som I drømmer om.'
        ]
      },
      {
        heading: 'Fra dæmpet middagsmusik til brudevals og propfyldt dansegulv',
        paragraphs: [
          'Et bryllup strækker sig ofte over mange timer, og musikken skal afspejle festens udvikling. Under middagen sørger jeg for en behagelig, dæmpet baggrundsmusik, der skaber den rette atmosfære uden at genere samtalerne ved bordene. Jeg koordinerer løbende med jeres toastmaster, så lydniveauet tilpasses perfekt til de forskellige indslag og taler.',
          'Når klokken nærmer sig midnat, er jeg klar til at time det store højdepunkt: Den traditionsrige brudevals. Herefter åbner jeg op for den helt store dansefest for alle jeres gæster – uanset om de er til klassiske disco-hits, svedige 90\'er-baskere, uundgåelige sing-along klassikere eller moderne pop. Med fire årtier bag pulten har jeg en unik evne til at spotte, hvad der skal til for at holde både bedsteforældre, onkler og venner i gang på dansegulvet på samme tid.'
        ]
      },
      {
        heading: 'Stilrent setup og kompromisløs kvalitet',
        paragraphs: [
          'Når du vælger DJ Ron9, vælger du en stilfuld løsning. Mit setup er elegant og diskret, så det komplementerer jeres bryllupslokation. Jeg benytter udelukkende pro-audio udstyr af højeste kvalitet, hvilket sikrer en professionel lyd, uanset om der spilles lavt eller højt, krydret med et flot og stemningsfuldt lysshow.'
        ]
      }
    ]
  },
  foedselsdag: {
    slug: 'foedselsdag',
    canonicalPath: '/dj-til-foedselsdag/',
    title: 'Professionel DJ til Fødselsdag',
    subtitle: 'Skal du holde 40, 50, 60 eller 70 års fødselsdag i København eller på Sjælland? Med +25 års erfaring samler DJ Ron9 alle generationer til dit livs brag af en fødselsdagsfest.',
    cta: 'Få et tilbud på DJ til fødselsdag',
    image: 'https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/djron9-foedselsdag.webp',
    imagePosition: 'object-center',
    formHeading: 'Book DJ Ron9 til din fødselsdag',
    sections: [
      {
        heading: 'Fejr din milepæl med den helt rigtige musik profil',
        paragraphs: [
          'En rund fødselsdag eller et stort jubilæum er den perfekte anledning til at samle familie, venner og kollegaer til en uforglemmelig aften. Men når gæstelisten spænder lige fra de unge teenagere til bedsteforældrene, kan det være en udfordring at finde en musikalsk balance, der gør alle glade. Det kræver en DJ, som besidder den nødvendige erfaring til at læse og styre et sammensat dansegulv..',
          'Med over 25 år i branchen som mobil DJ i København og omegn har jeg spillet til hundredvis af fødselsdage og jubilæer. Min store styrke er min evne til at navigere på tværs af genrer og årtier. Jeg ved præcis, hvornår det er tid til at spille de nostalgiske hits fra din ungdom, hvornår der skal skrues op for de klassiske fællessange, og hvornår de moderne radiohits skal holde energien oppe i de sene nattetimer.'
        ]
      },
      {
        heading: 'Skræddersyet efter din smag og dine gæster',
        paragraphs: [
          'Din fødselsdagsfest skal afspejle dig. Derfor baserer jeg altid mit arbejde på en tæt dialog med dig inden festen. Vi taler om, hvilke genrer du elsker – uanset om det er klassisk 80\'er pop, svedig rock, sprød funk eller de største club-baskere fra 90\'erne og 00\'erne. Lige så vigtigt er det, at vi får styr på dine "no-go"-sange, så der ikke opstår ubehagelige overraskelser.',
          'Under selve middagen sørger jeg for en behagelig, afdæmpet baggrundsmusik, der understøtter den gode stemning og lader gæsterne tale sammen. Jeg står klar med mikrofoner til eventuelle taler og koordinerer tæt med dig eller festens toastmaster, så overgangen til selve dansefesten bliver fuldstændig glidende og timet perfekt.'
        ]
      },
      {
        heading: 'Komplet og professionel total løsning på hele Sjælland',
        paragraphs: [
          'Når du booker DJ Ron9, skal du ikke bekymre dig om det tekniske. Jeg medbringer et komplet, professionelt lydanlæg og et flot, stemningsfuldt lysshow, der tilpasses jeres festlokale – uanset om festen holdes i et lejet forsamlingshus, et telt i haven, eller på en restaurant. Du får en moden, pålidelig og top-professionel DJ, der sætter en stor ære i at levere varen, så du trygt kan slappe af og nyde din store dag sammen med dine gæster.'
        ]
      }
    ]
  },
  'dj-og-saxofon': {
    slug: 'dj-og-saxofon',
    canonicalPath: '/dj-og-saxofon/',
    title: 'DJ og Live Saxofon – Den ultimative premium-oplevelse på Sjælland',
    subtitle: 'Løft dit event til næste niveau. Kombinationen af DJ Ron9 og en professionel live-saxofonist skaber en magnetisk, eksklusiv klubstemning.',
    cta: 'Få et tilbud på DJ og saxofon',
    image: 'https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/djron9-dj-saxofon.webp',
    imagePosition: 'object-[center_20%]',
    formHeading: 'Hør mere om DJ & Saxofon til jeres event',
    isSaxofon: true,
    sections: [
      {
        heading: 'Den perfekte synergi mellem det digitale og det levende',
        paragraphs: [
          'Vil du give dine gæster en musikalsk oplevelse, der skiller sig ud? Konceptet med en professionel mobil DJ kombineret med en live-saxofonist er blevet et af de mest eftertragtede set-ups til både bryllupper, privatfester og firmafester. Det leverer det absolut bedste fra to verdener: DJ\'ens evne til at spille præcis de hits, folk elsker, smeltet sammen med den rå, organiske energi og improvisation, som kun en saxofonist kan præstere.',
          'Når jeg spiller sammen med en professionel saxofonist, arbejder vi som en tæt enhed. Saxofonisten spiller med ind over mine sprøde klub- og pop-beats, improviserer over de fængende melodilinjer og bevæger sig ofte ud på selve dansegulvet blandt gæsterne. Det skaber en helt unik, interaktiv kemi, der trækker folk til og giver et fantastisk visuelt "wow-moment".'
        ]
      },
      {
        heading: 'Perfekt til både den stilfulde reception og den vilde nattefest',
        paragraphs: [
          'Alsidigheden i DJ & Saxofon set-up gør det velegnet til flere forskellige faser af din fest. Til en virksomhedsreception, et sommer-event eller under velkomstdrikken til et bryllup kan vi levere dæmpede, tilbagelænede Ibiza-lounge-vibes, soul og blød jazz, der oser af overskud og stil.',
          'Når mørket falder på, og dansefesten skal skydes i gang, skruer vi op for energien. Saxofonisten leverer energiske soloer over alt fra moderne house-klassikere til velkendte 80\'er- og 90\'er-hits. Med over 25 års erfaring som DJ sikrer jeg, at fundamentet og timingen sidder lige i skabet, så saxofonisten har det perfekte lærred at male sine toner på.'
        ]
      },
      {
        heading: 'En eksklusiv totaloplevelse skræddersyet til dig',
        paragraphs: [
          'Dette set-up er til dig, der ikke vil nøjes, men i stedet søger en eksklusiv feststemning. Jeg medbringer alt nødvendigt high-end pro-audio lydudstyr samt professionelle trådløse mikrofonsystemer til saxofonen, så musikerens mobilitet er i top. Hvert arrangement planlægges og koordineres nøje, så lydniveau, musikvalg og visuelt udtryk passer perfekt til jeres fest.'
        ]
      }
    ]
  },
  'vinyl-dj': {
    slug: 'vinyl-dj',
    canonicalPath: '/vinyl-dj/',
    title: 'Vinyl DJ i København – Den autentiske lyd af ægte grammofonplader',
    subtitle: 'Oplev charmen, nostalgien og den uovertrufne varme lyd, når DJ Ron9 ruller det klassiske set-up ud med vinylplader.',
    cta: 'Book en autentisk Vinyl DJ',
    image: 'https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/header-billede-vinyl-dj.jpeg',
    imagePosition: 'object-[50%_33%]',
    formHeading: 'Bestil et uforpligtende tilbud på Vinyl DJ',
    sections: [
      {
        heading: 'Det gamle håndværk bragt til live til din fest',
        paragraphs: [
          'I en digitaliseret tidsalder, hvor musik ofte er reduceret til streaming og digitale filer, har den analoge vinylplade bevaret sin helt særlige magi. At booke en Vinyl DJ handler om meget mere end blot musikken – det handler om atmosfære, nostalgi og et dybtfølt musikalsk håndværk. Når jeg ruller mit dedikerede vinyl-setup ud, tager jeg jer med på en tidsrejse tilbage til dengang, hvor musik var noget, man kunne røre ved, se og føle.',
          'Med over 25 års erfaring bag pulten har jeg oplevet vinyl-æraen på første hånd. For mig er det ikke en retro-trend; det er mit fundamentale håndværk. Gæsterne vil kunne se mig bladre i pladekasserne, placere pick-up\'en på pladen og mixe live på to pladespillere. Det giver et visuelt udtryk, som fungerer som et fantastisk samtaleemne til enhver fest, og som med det samme signalerer tårnhøj musikalsk autoritet.'
        ]
      },
      {
        heading: 'Sprød lyd og de største klassikere fra de sidste fire årtier',
        paragraphs: [
          'Den største fordel ved at vælge et vinyl-setup til dit event i København er lydkvaliteten. Vinylplader leverer en helt speciel dynamisk dybde og en karakteristisk, varm "knitren", som digitale filer simpelthen ikke kan genskabe.',
          'Mit pladekartotek spænder bredt og rummer de største skatte fra musikhistorien. Jeg leverer alt fra de svedigste funk- og soul-klassikere fra 70\'erne, de ikoniske pop- og rock-baskere fra 80\'erne, til den tidlige, sprøde 90\'er-hiphop og eurodance. Uanset om du ønsker et rendyrket retro-tema, en stilfuld reception med sprøde jazz- og loungeskiver, eller en intim fødselsdagsfest med ungdommens soundtrack, tilpasses pladekasserne specifikt efter dine ønsker.'
        ]
      },
      {
        heading: 'Et stilrent og nostalgisk blikfang',
        paragraphs: [
          'Et Vinyl DJ-setup kræver sit eget udtryk. Jeg medbringer et flot, dedikeret setup med klassiske pladespillere, som præsenterer sig utrolig skarpt i lokalet. Kombineret med moderne, krystalklare pro-audio højttalere sikrer jeg, at den analoge lyd slipper fuldstændig til sin ret, uanset om der spilles dæmpet receptionsmusik eller op til intens dans. Det er den ultimative autentiske løsning til din fest.'
        ]
      }
    ]
  },
  'dj-til-fest': {
    slug: 'dj-til-fest',
    canonicalPath: '/dj-ron9-fylder-dit-dansegulv/dj-til-fest/',
    title: 'Professionel DJ til Fest & Events i København og på Sjælland',
    subtitle: 'DJ Ron9 fylder dit dansegulv med den perfekte musik tilpasset jeres fest – med over 25 års erfaring og professionelt lyd- og lysudstyr.',
    cta: 'Få et tilbud på DJ til fest',
    image: 'https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/38a16fa4-1397-425f-b1c5-c7a9fe914d63.jpg',
    imagePosition: 'object-center',
    formHeading: 'Book DJ Ron9 til din næste fest',
    sections: [
      {
        heading: 'DJ Ron9 fylder dit dansegulv til festen',
        paragraphs: [
          'Skal du holde en stor fest, et jubilæum eller en privat sammenkomst, hvor musikken skal sidde lige i skabet? Med over 25 års erfaring bag pulten sikrer DJ Ron9 et fyldt dansegulv og god stemning hele aftenen.',
          'Musikken tilpasses dine og gæsternes ønsker med en skarp fornemmelse for rummet og timing – lige fra den stemningsfulde velkomstmusik til de sene nattetimers vilde dansefest.'
        ]
      },
      {
        heading: 'Professionelt lyd- og lysudstyr tilpasset lokalet',
        paragraphs: [
          'Jeg medbringer komplet, professionelt lyd- og lysudstyr af højeste kvalitet, som tilpasses lokalets størrelse og akustik. Du får en moden og pålidelig DJ, der sørger for en gnidningsfri afvikling fra start til slut.'
        ]
      }
    ]
  }
}

export default function ServiceDetail({ defaultSlug }) {
  const { slug: paramSlug } = useParams()
  const [reviews, setReviews] = useState([])
  const [faqs, setFaqs] = useState([])
  const [openFaq, setOpenFaq] = useState(null)

  // Map slug aliases
  let rawSlug = (defaultSlug || paramSlug || (typeof window !== 'undefined' ? window.location.pathname : '')).toLowerCase()
  let currentKey = rawSlug
  if (currentKey.includes('bryllup')) currentKey = 'bryllup'
  else if (currentKey.includes('firmafest')) currentKey = 'firmafest'
  else if (currentKey.includes('foedselsdag') || currentKey.includes('fødselsdag')) currentKey = 'foedselsdag'
  else if (currentKey.includes('saxofon') || currentKey.includes('sax')) currentKey = 'dj-og-saxofon'
  else if (currentKey.includes('vinyl')) currentKey = 'vinyl-dj'
  else if (currentKey.includes('fest') || currentKey.includes('dansegulv')) currentKey = 'dj-til-fest'

  const service = SERVICES_DATA[currentKey] || SERVICES_DATA['bryllup']
  const canonicalPath = service.canonicalPath || `/${service.slug}/`
  const optimizedServiceImage = service.image?.startsWith('https://assets.auxodev.dk/')
    ? `https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/${service.image.replace('https://assets.auxodev.dk/', '')}`
    : service.image

  const serviceTitles = {
    bryllup: 'DJ til Bryllup i København & Sjælland | DJ Ron9',
    firmafest: 'DJ til Firmafest & Fredagsbar i København & Sjælland | DJ Ron9',
    foedselsdag: 'DJ til Fødselsdag & Rund Fødselsdag | DJ Ron9',
    'dj-og-saxofon': 'DJ & Saxofon til Fest & Bryllup | DJ Ron9',
    'vinyl-dj': 'Vinyl DJ til Eksklusive Fester & Events | DJ Ron9',
    'dj-til-fest': 'DJ til Fest – DJ Ron9 fylder dit dansegulv | DJ Ron9',
  }

  const serviceDescriptions = {
    bryllup: 'Erfaren DJ til bryllup i København og på Sjælland med over 25 års erfaring. Skab tryghed og et fyldt dansegulv fra brudevalsen til den vilde fest.',
    firmafest: 'Professionel DJ til firmafest og events. Musik for alle generationer, elegant lyd- og lyssetup samt dansegaranti til jeres firmaarrangement.',
    foedselsdag: 'Få et kogende dansegulv til din fødselsdagsfest med DJ Ron9. Stilsikkert musikmix af 80er, 90er hits og nye klassikere tilpasset jeres gæster.',
    'dj-og-saxofon': 'Eksklusiv kombination af DJ og live saxofonist til bryllup, reception eller firmafest. Giv jeres gæster en uforglemmelig musikoplevelse.',
    'vinyl-dj': 'Autentisk Vinyl DJ med ægte pladespillere, varm lyd og stilfuldt udtryk til reception, bryllup eller jubilæum i København og på Sjælland.',
    'dj-til-fest': 'DJ Ron9 leverer den perfekte festmusik med professionelt lyd- og lysudstyr tilpasset jeres fest, så dansegulvet holdes fyldt hele natten.',
  }

  const serviceSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `https://www.djron9.dk${canonicalPath}#service`,
        "name": service.title,
        "description": service.subtitle,
        "provider": {
          "@type": "EntertainmentBusiness",
          "name": "DJ Ron9",
          "url": "https://www.djron9.dk/",
          "telephone": "+4550998282",
          "email": "ronni@djron9.dk"
        },
        "areaServed": [
          { "@type": "City", "name": "København" },
          { "@type": "AdministrativeArea", "name": "Sjælland" }
        ],
        "image": service.image
      },
      {
        "@type": "BreadcrumbList",
        "@id": `https://www.djron9.dk${canonicalPath}#breadcrumb`,
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://www.djron9.dk/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Ydelser",
            "item": "https://www.djron9.dk/#ydelser"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": service.title,
            "item": `https://www.djron9.dk${canonicalPath}`
          }
        ]
      },
      ...(faqs && faqs.length > 0
        ? [
            {
              "@type": "FAQPage",
              "mainEntity": faqs.map((faq) => ({
                "@type": "Question",
                "name": faq.question?.da || faq.question || "",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": (faq.answer?.da || faq.answer || "").replace(/<[^>]*>?/gm, "").trim()
                }
              }))
            }
          ]
        : [])
    ]
  }

  useEffect(() => {
    // Hent anmeldelser og filtrér specifikt til sidens eventtype
    fetch('/_api/public/content/reviews')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          let matched = []

          if (currentKey === 'firmafest') {
            // Filtrér anmeldelser til firmafest / erhverv / julefrokost
            matched = data.filter((r) => {
              const type = (r.event_type?.da || r.event_type || '').toLowerCase()
              const name = (r.name || '').toLowerCase()
              return (
                type.includes('firma') ||
                type.includes('julefrokost') ||
                type.includes('corporate') ||
                type.includes('erhverv') ||
                name.includes('kraftvaerk') ||
                name.includes('medtronic') ||
                name.includes('industriens pension') ||
                name.includes('tryg') ||
                name.includes('einar kornerup')
              )
            })

            // Sortér så Ria Karpinen / Ria Karppinen (Medtronic) placeres helt øverst/først
            matched.sort((a, b) => {
              const aIsRia = (a.name || '').toLowerCase().includes('ria') || (a.name || '').toLowerCase().includes('karp')
              const bIsRia = (b.name || '').toLowerCase().includes('ria') || (b.name || '').toLowerCase().includes('karp')
              if (aIsRia) return -1
              if (bIsRia) return 1
              return 0
            })
          } else if (currentKey === 'bryllup') {
            // Filtrér anmeldelser til bryllup og sølvbryllup (uden rene saxofon-kombinationer)
            matched = data.filter((r) => {
              const type = (r.event_type?.da || r.event_type || '').toLowerCase()
              return (type.includes('bryllup') || type.includes('sølvbryllup')) && !type.includes('sax')
            })
          } else if (currentKey === 'foedselsdag') {
            // Filtrér anmeldelser til fødselsdag / runde fødselsdage
            matched = data.filter((r) => {
              const type = (r.event_type?.da || r.event_type || '').toLowerCase()
              return (
                (type.includes('fødselsdag') ||
                  type.includes('foedselsdag') ||
                  type.includes('års') ||
                  type.includes('party')) &&
                !type.includes('firma') &&
                !type.includes('bryllup')
              )
            })
          } else if (currentKey === 'dj-og-saxofon') {
            // Filtrér anmeldelser specifikt til DJ & Saxofon
            matched = data.filter((r) => {
              const type = (r.event_type?.da || r.event_type || '').toLowerCase()
              const text = (r.text?.da || r.text || '').toLowerCase()
              return type.includes('sax') || text.includes('saxofon') || text.includes('sax')
            })
          } else if (currentKey === 'vinyl-dj') {
            // Filtrér anmeldelser til Vinyl DJ / retro / 70'er, 80'er, 90'er musik
            matched = data.filter((r) => {
              const name = (r.name || '').toLowerCase()
              if (name.includes('anne vedel')) return false // Holdes på fødselsdagssiden
              const type = (r.event_type?.da || r.event_type || '').toLowerCase()
              const text = (r.text?.da || r.text || '').toLowerCase()
              return (
                type.includes('vinyl') ||
                text.includes('vinyl') ||
                type.includes('80') ||
                text.includes('70’er') ||
                text.includes("70'er") ||
                text.includes('80’er') ||
                text.includes("80'er") ||
                text.includes('90’er') ||
                text.includes("90'er") ||
                text.includes('nostalgi')
              )
            })

            // Sortér så retro-specifikke anmeldelser prioriteres helt i top (f.eks. Anna Kit med 80'er party)
            matched.sort((a, b) => {
              const aName = (a.name || '').toLowerCase()
              const bName = (b.name || '').toLowerCase()
              if (aName.includes('anna kit')) return -1
              if (bName.includes('anna kit')) return 1
              if (aName.includes('line brinch')) return -1
              if (bName.includes('line brinch')) return 1
              return 0
            })
          }

          if (matched.length === 0) {
            matched = data
          }

          setReviews(matched.slice(0, 3))
        }
      })
      .catch((err) => console.error('Fejl ved hentning af anmeldelser:', err))

    // Hent FAQs
    fetch('/_api/public/content/faqs')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setFaqs(data.slice(0, 3))
        }
      })
      .catch((err) => console.error('Fejl ved hentning af FAQs:', err))
  }, [currentKey])

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  return (
    <div className="bg-black text-white">
      <SEO
        title={serviceTitles[currentKey] || `${service.title} | DJ Ron9`}
        description={serviceDescriptions[currentKey] || service.subtitle}
        canonical={canonicalPath}
        ogImage={service.image}
        schema={serviceSchema}
      />
      {/* Hero Section */}
      <section className="relative flex min-h-[40vh] sm:min-h-[55vh] items-center justify-center overflow-hidden py-12 sm:py-20">
        {/* Header Image as Responsive Cover */}
        <div className="absolute inset-0 z-0">
          <img
            src={optimizedServiceImage || service.image}
            alt={service.title}
            width="1600"
            height="600"
            fetchPriority="high"
            decoding="async"
            className={`h-full w-full object-cover opacity-60 scale-100 ${service.imagePosition || 'object-center'}`}
            style={service.slug === 'vinyl-dj' ? { objectPosition: '50% 33%' } : undefined}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/40" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            {/* Single H1 per page */}
            <h1 className="font-serif text-3xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl text-white leading-tight">
              {service.title}
            </h1>

            {/* Subtitle paragraph */}
            <p className="mt-6 text-sm text-zinc-200 sm:text-lg leading-relaxed font-normal">
              {service.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
              <button
                onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#bb9b56] px-8 py-3.5 text-sm font-bold text-white hover:bg-[#a88645] transition-all shadow-lg hover:shadow-amber-500/10 cursor-pointer"
              >
                <Send className="h-4 w-4" />
                {service.cta}
              </button>
              <Link
                to="/anmeldelser"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-white/80 bg-transparent px-8 py-3.5 text-sm font-bold text-white transition-all hover:bg-white/10"
              >
                <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                Se mine anmeldelser her
              </Link>
            </div>
          </div>
        </div>

        {/* Decorative bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black to-transparent" />
      </section>

      {/* Featured Single Review Strip - Right below banner area */}
      {reviews.length > 0 && (() => {
        const featuredReview = reviews[0]
        const rating = featuredReview.rating || 5
        return (
          <section className="border-b border-zinc-900 bg-zinc-950/60 py-3 sm:py-4">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="relative overflow-hidden rounded-xl border border-amber-500/30 shadow-backlight-hover bg-zinc-900/60 md:shadow-backlight md:border-zinc-800/80 md:bg-zinc-900/40 md:hover:shadow-backlight-hover md:hover:border-amber-500/30 px-4 py-3 sm:px-6 sm:py-3.5 backdrop-blur-sm transition-all duration-300">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5 md:gap-6">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0 flex-1">
                    {/* Stars & Author */}
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-center gap-0.5 text-[#bb9b56]">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3.5 w-3.5 ${i < rating ? 'fill-[#bb9b56] text-[#bb9b56]' : 'text-zinc-700'}`}
                          />
                        ))}
                      </div>
                      <span className="text-xs font-bold text-white whitespace-nowrap">
                        {featuredReview.name}
                      </span>
                    </div>

                    {/* Review text */}
                    <p className="text-xs sm:text-sm text-zinc-300 italic truncate leading-normal">
                      "{featuredReview.text?.da || featuredReview.text}"
                    </p>
                  </div>

                  {/* Link to all reviews */}
                  <div className="shrink-0 flex items-center justify-end">
                    <Link
                      to="/anmeldelser"
                      className="text-xs font-semibold text-[#bb9b56] hover:text-amber-400 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                    >
                      Læs anmeldelser
                      <span aria-hidden="true">&rarr;</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )
      })()}

      {/* Main Content & Sections */}
      <section className="py-16 bg-black">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            {/* Left Column: Text Content with H2 Headings */}
            <div className="lg:col-span-7 space-y-10">
              {service.sections.map((sec, idx) => (
                <div key={idx} className="space-y-4">
                  <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white tracking-tight">
                    {sec.heading}
                  </h2>
                  {sec.paragraphs.map((pText, pIdx) => (
                    <p key={pIdx} className="text-zinc-300 text-base sm:text-lg leading-relaxed">
                      {pText}
                    </p>
                  ))}
                </div>
              ))}

            </div>

            {/* Right Column: Contact Form with exact requested section title */}
            <div id="kontakt" className="lg:col-span-5 space-y-6">
              <ContactForm defaultService={service.slug} title={service.formHeading} />

              {service.slug === 'vinyl-dj' && (
                <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950/80 shadow-2xl transition-all duration-300">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/vinyl-dj-set-up.jpg"
                    alt="Vinyl DJ set-up"
                    width="600"
                    height="400"
                    decoding="async"
                    className="w-full h-auto object-cover rounded-2xl"
                    loading="lazy"
                  />
                </div>
              )}

              {(service.slug === 'foedselsdag' || service.slug === 'dj-og-saxofon') && (reviews[1] || reviews[0]) && (
                <div>
                  <ReviewCard review={reviews[1] || reviews[0]} />
                </div>
              )}
            </div>
          </div>

          {/* Interactive Checklist (for Wedding page) - Fuld bredde */}
          {service.isWedding && (
            <div className="mt-16 w-full">
              <WeddingChecklist />
            </div>
          )}
        </div>
      </section>

      {/* References / Name-Dropping Section */}
      {service.references && service.references.length > 0 && (
        <section className="py-20 bg-zinc-950/80 border-t border-zinc-900 relative overflow-hidden">
          {/* Subtle background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#bb9b56]/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#bb9b56]/10 border border-[#bb9b56]/30 text-[#bb9b56] text-xs font-semibold uppercase tracking-wider mb-4">
                <Building2 className="h-3.5 w-3.5" />
                Referencer & Erhvervskunder
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
                Virksomheder & Lokationer
              </h2>
              <p className="mt-4 text-base sm:text-lg text-zinc-300 leading-relaxed">
                Gennem mere end 25 år som professionel DJ har jeg haft fornøjelsen af at levere musik og feststemning til firmafester, receptioner, jubilæer og fredagsbarer for en lang række anerkendte virksomheder og ikoniske venues.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-2.5">
              {service.references.map((company, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center px-3 py-2 sm:px-3.5 sm:py-2.5 rounded-lg bg-zinc-900/50 border border-zinc-800/80 hover:border-[#bb9b56]/60 hover:bg-zinc-900 transition-all duration-200 text-center group min-h-[42px]"
                >
                  <span className="text-xs sm:text-sm font-medium text-zinc-300 group-hover:text-white transition-colors truncate">
                    {company}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-xs sm:text-sm text-zinc-400">
                Samt mange flere offentlige institutioner, organisationer, hoteller og private virksomheder.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Equipment / Pro Audio Section */}
      <section className="py-20 bg-zinc-950/60 border-t border-zinc-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
            <div className="space-y-6">
              <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl text-white">
                Professionelt lyd- og lysudstyr
              </h2>
              <p className="text-zinc-300 leading-relaxed text-base">
                Kvaliteten af udstyret er afgørende for den samlede oplevelse. Jeg medbringer udelukkende high-end udstyr fra førende producenter for at sikre krystalklar lyd og et fantastisk lysshow.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
                <div className="flex gap-3">
                  <Volume2 className="h-6 w-6 text-[#bb9b56] shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-white">Krystalklar lyd</h3>
                    <p className="text-xs text-zinc-400 mt-1">Professionelle højttalere og subwoofere tilpasset lokalets akustik.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Lightbulb className="h-6 w-6 text-[#bb9b56] shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-white">Intelligent lysshow</h3>
                    <p className="text-xs text-zinc-400 mt-1">Bevægelige lamper og lyseffekter, der skaber den ægte club-vibe.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Sliders className="h-6 w-6 text-[#bb9b56] shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-white">Stilfuld pult</h3>
                    <p className="text-xs text-zinc-400 mt-1">En elegant, minimalistisk DJ-pult, der passer ind i ethvert festlokale.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Music className="h-6 w-6 text-[#bb9b56] shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-white">Trådløse mikrofoner</h3>
                    <p className="text-xs text-zinc-400 mt-1">Professionelle mikrofoner til rådighed for taler og indslag.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <video
                src="/Video/professionel.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="none"
                className="rounded-2xl object-cover w-full h-[350px] object-top shadow-2xl border border-zinc-800"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="py-20 bg-black border-t border-zinc-900">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5 relative order-2 lg:order-1">
                <video
                  src={service.isSaxofon ? "/Video/profess.mp4" : service.isWedding ? "/Video/DJ-header-video_2.mp4" : "/Video/føds.mp4"}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="none"
                  className="rounded-2xl object-cover w-full h-[450px] object-center border border-[#bb9b56]/40 shadow-2xl"
                />
              </div>

              <div className="lg:col-span-7 space-y-8 order-1 lg:order-2">
                <div>
                  <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl text-white">
                    Ofte stillede spørgsmål
                  </h2>
                  <p className="mt-4 text-zinc-400">
                    Få hurtigt svar på de mest almindelige spørgsmål vedrørende booking og afvikling.
                  </p>
                </div>

                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div
                      key={faq.id}
                      className="rounded-xl border border-zinc-800 bg-zinc-950/60 overflow-hidden transition-all duration-300"
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        aria-expanded={openFaq === index}
                        aria-controls={`service-faq-answer-${faq.id || index}`}
                        id={`service-faq-question-${faq.id || index}`}
                        className="flex w-full items-center justify-between px-6 py-5 text-left font-medium text-white hover:text-[#bb9b56] focus:outline-none"
                      >
                        <span>{faq.question?.da}</span>
                        <ChevronDown
                          className={`h-5 w-5 text-zinc-500 transition-transform duration-200 ${
                            openFaq === index ? 'rotate-180 text-[#bb9b56]' : ''
                          }`}
                        />
                      </button>

                      <div
                        id={`service-faq-answer-${faq.id || index}`}
                        role="region"
                        aria-labelledby={`service-faq-question-${faq.id || index}`}
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${
                          openFaq === index ? 'max-h-[500px] border-t border-zinc-900' : 'max-h-0'
                        }`}
                      >
                        <div
                          className="px-6 py-5 text-sm text-zinc-400 leading-relaxed prose prose-invert max-w-none"
                          dangerouslySetInnerHTML={{ __html: faq.answer?.da }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

    </div>
  )
}
